import { Link } from "react-router-dom";
import { Globe2, Heart, Sparkles, ArrowRight, Quote } from "lucide-react";

const PILLARS = [
  {
    icon: Globe2,
    title: "African voices first",
    body: "We center filmmakers from Lagos to Nairobi, from Kigali to Cape Town. The continent has carried the Gospel for two millennia — its stories deserve a stage built for them.",
  },
  {
    icon: Heart,
    title: "Faith over formula",
    body: "Every approved title points back to Christ. We're not a religious tag on a generic platform — moderation is curatorial, not algorithmic.",
  },
  {
    icon: Sparkles,
    title: "Creators eat first",
    body: "Revenue is shared transparently with the people who make the work. Naira-first payouts. No opaque deals. Real dashboards.",
  },
];

export default function About() {
  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-16 lg:py-24" data-testid="about-page">
      {/* Hero */}
      <p className="text-eyebrow text-[#E65F2B]">Our story</p>
      <h1 className="font-display text-5xl sm:text-7xl font-black tracking-tighter mt-3 leading-[0.95]" data-testid="about-headline">
        Christian cinema,
        <br />
        <span className="text-[#E65F2B]">African-led.</span>
      </h1>
      <p className="text-white/60 text-lg max-w-2xl mt-6 leading-relaxed">
        Christoreel is a streaming home for African Christian movies. We exist so the next generation of African filmmakers can tell faith-rooted stories — and get paid for them — without bending to gatekeepers thousands of miles away.
      </p>

      {/* Mission card */}
      <div className="card-surface p-8 sm:p-12 mt-16 relative overflow-hidden">
        <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full bg-[#E65F2B]/10 blur-3xl" aria-hidden />
        <div className="relative">
          <Quote className="h-8 w-8 text-[#E65F2B] mb-4" />
          <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight">
            We're building the platform the continent's filmmakers deserve.
          </h2>
          <p className="text-white/60 mt-4 max-w-3xl">
            Mainstream streaming buys African content piecemeal, on terms that rarely return value to its creators. Faith-focused platforms ignore the continent entirely. We started Christoreel to close both gaps in a single offering: a Netflix-style experience, scoped to Christian content, owned and operated for an African audience.
          </p>
        </div>
      </div>

      {/* Pillars */}
      <div className="mt-16">
        <p className="text-eyebrow mb-6">What we stand for</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PILLARS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="card-surface p-6" data-testid={`pillar-${title.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="h-11 w-11 rounded-lg flex items-center justify-center bg-[#E65F2B]/15 border border-[#E65F2B]/30 mb-4">
                <Icon className="h-5 w-5 text-[#E65F2B]" />
              </div>
              <h3 className="font-display text-lg font-bold">{title}</h3>
              <p className="text-sm text-white/60 mt-2 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* How it works */}
      <div className="mt-20 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-8 items-start">
        <div>
          <p className="text-eyebrow text-[#E65F2B] mb-3">For viewers</p>
          <h3 className="font-display text-3xl font-bold">Unlimited streaming, one Naira-priced plan.</h3>
          <p className="text-white/60 mt-3">
            One subscription, every approved title. We curate every release ourselves — no algorithmic shock, no content that contradicts the faith we serve. Cancel anytime.
          </p>
          <Link to="/subscription" className="ember-btn mt-6 inline-flex" data-testid="about-cta-viewers">
            See the plan <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="hidden lg:flex items-center justify-center h-full">
          <div className="h-32 w-px bg-white/10" />
        </div>

        <div>
          <p className="text-eyebrow text-[#EAB308] mb-3">For creators</p>
          <h3 className="font-display text-3xl font-bold">Bring your story. Keep the credit.</h3>
          <p className="text-white/60 mt-3">
            Set up your channel in minutes. Upload your film. Get reviewed by a real human within 48 hours. Earn a transparent share of subscriber revenue tied to actual watch time.
          </p>
          <Link to="/submission-guidelines" className="ghost-btn mt-6 inline-flex" data-testid="about-cta-creators">
            Read submission guidelines <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Final CTA strip */}
      <div className="card-surface p-8 sm:p-10 mt-20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
        <div className="flex items-center gap-4">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full overflow-hidden bg-[#FDF0CA]">
            <img src="/logo192.png" alt="Christoreel" className="h-10 w-10 object-cover" />
          </span>
          <div>
            <p className="font-display text-xl font-bold">Built to honor the storytellers.</p>
            <p className="text-xs text-white/40 mt-1">Naira-first · African-led · Faith-rooted</p>
          </div>
        </div>
        <Link to="/contact" className="ember-btn whitespace-nowrap" data-testid="about-cta-contact">
          Talk to us <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
